import React, { Fragment, useCallback, useEffect, useMemo, type UIEvent } from "react";
import { authManager } from "@/lib/managers/auth-manager";
import { NotificationStore } from "@/stores/notification-store.core";
import { reportError } from "@/utils/logging-utils";
import type { NotifiableError } from "@bugsnag/js";
import {
   NotificationAction,
   NotificationEntity,
} from "@laborchart-modules/common/dist/rethink/schemas/enums/notifications";
import type { SerializedNotification } from "@laborchart-modules/lc-core-api/dist/api/notifications/get-user-notifications";
import {
   Button,
   Card,
   EmptyState,
   Flex,
   H2,
   Spinner,
   Title,
   Typography,
   useI18nContext,
} from "@procore/core-react";
import { formatDateTime } from "@procore/globalization-toolkit";
import { useToastAlertContext } from "@procore/toast-alert";
import { useInfiniteQuery } from "@tanstack/react-query";
import { onPersonDetailClick, usePersonTearsheet } from "../tearsheets/people/people-tearsheet";
import { onProjectDetailClick, useProjectTearsheet } from "../tearsheets/project/project-tearsheet";
import { NotificationBanner } from "./notification-banner";
import { isNotificationNameType } from "@/lib/utils/notification-formatter";
import { router } from "@/lib/router";
import "./css/home.css";

const NEW_GANTT_PATH = "/groups/my-groups/new-gantt";
const ITEMS_PER_REQUEST = 50;
const SCROLL_THRESHOLD = 10;
const CONTAINER_VERTICAL_SPACING = "2rem";
const SCROLL_AREA_VERTICAL_SPACING = "17rem";
const FIRST_PAGE = 0;

const getNotifications = async ({
   pageParam,
}: {
   pageParam: number;
}): Promise<SerializedNotification[]> => {
   const result = await NotificationStore.getNotifications({
      skip: pageParam * ITEMS_PER_REQUEST,
      limit: ITEMS_PER_REQUEST,
   }).payload;
   return result.data;
};

export const isUnread = (notification: SerializedNotification): boolean => {
   const userID = authManager.authedUserId();
   if (userID) {
      return !notification.viewed_by_ids.includes(userID);
   }
   return true;
};

export const NotificationsCard = () => {
   const I18n = useI18nContext();
   const { showToast } = useToastAlertContext();
   const { dispatch: personTearsheetDispatch } = usePersonTearsheet();
   const { dispatch: projectTearsheetDispatch } = useProjectTearsheet();
   const { data, error, fetchNextPage, hasNextPage, isFetching, isFetchingNextPage, refetch } =
      useInfiniteQuery({
         queryKey: ["notifications"],
         queryFn: getNotifications,
         initialPageParam: FIRST_PAGE,
         getNextPageParam: (lastPage, _allPages, lastPageParam) =>
            lastPage.length < ITEMS_PER_REQUEST ? undefined : lastPageParam + 1,
      });
   const notifications = useMemo(() => data?.pages.flat() || [], [data]);
   const hasUnreadNotifications = useMemo(() => notifications.some(isUnread), [notifications]);

   useEffect(() => {
      if (error) {
         console.error("Error getNotifications: ", error);
         reportError(error, "NotificationsCard - getNotifications");
         showToast.error(
            I18n.t(
               "views.company.workforce_planning.home.notifications.error_loading_notifications",
            ),
         );
      }
   }, [error]);

   const navigateToNotification = useCallback(
      async (notification: SerializedNotification): Promise<void> => {
         try {
            const success = await NotificationStore.notificationViewed(notification.id).payload;
            if (success.data.success) {
               if (notification.entity === NotificationEntity.PERSON) {
                  onPersonDetailClick(
                     personTearsheetDispatch,
                     notification.subject_1_id,
                     undefined,
                     refetch,
                  );
               } else if (notification.entity === NotificationEntity.PROJECT) {
                  onProjectDetailClick(
                     projectTearsheetDispatch,
                     notification.subject_1_id,
                     undefined,
                     refetch,
                  );
               } else if (
                  notification.entity === NotificationEntity.ASSIGNMENT ||
                  notification.entity === NotificationEntity.REQUEST
               ) {
                  const filterParams = new URLSearchParams();
                  if (notification.subject_1_name) {
                     filterParams.set(
                        "subjectName",
                        isNotificationNameType(notification.subject_1_name)
                           ? `${notification.subject_1_name.first} ${notification.subject_1_name.last}`
                           : notification.subject_1_name,
                     );
                  }
                  router.navigate(null, `${NEW_GANTT_PATH}?${filterParams.toString()}`);
               }
            }
         } catch (error) {
            console.error("Error navigateToNotification: ", error);
            reportError(error as NotifiableError, "NotificationsCard - navigateToNotification");
            showToast.error(
               I18n.t(
                  "views.company.workforce_planning.home.notifications.error_opening_notification_subject",
               ),
            );
         }
      },
      [personTearsheetDispatch, projectTearsheetDispatch, refetch, showToast, I18n],
   );

   const clearAllNotifications = useCallback(async (): Promise<void> => {
      try {
         const success = await NotificationStore.clearAllNotifications().payload;
         if (success) {
            refetch();
         }
      } catch (error) {
         console.error("Error clearAllNotifications: ", error);
         reportError(error as NotifiableError, "NotificationsCard - clearAllNotifications");
         showToast.error(
            I18n.t(
               "views.company.workforce_planning.home.notifications.error_clearing_all_notifications",
            ),
         );
      }
   }, [refetch, showToast, I18n]);

   const scrollNotifications = useCallback(
      (event: UIEvent<HTMLDivElement>) => {
         const { scrollTop, clientHeight, scrollHeight } = event.currentTarget;
         const isAtBottom = scrollTop + clientHeight >= scrollHeight - SCROLL_THRESHOLD;

         if (isAtBottom && hasNextPage && !isFetchingNextPage && !isFetching) {
            fetchNextPage();
         }
      },
      [hasNextPage, isFetchingNextPage, isFetching, fetchNextPage],
   );

   return (
      <Card
         style={{
            padding: "1rem",
            flex: "1 1 auto",
            minWidth: 0,
            maxHeight: `calc(100% - ${CONTAINER_VERTICAL_SPACING})`,
            borderRadius: "0.5rem",
         }}
      >
         <Flex direction="column" gap="sm">
            <Title style={{ alignSelf: "stretch" }}>
               <Title.Text>
                  <H2>{I18n.t("views.company.workforce_planning.home.notifications.title")}</H2>
               </Title.Text>
               {(!isFetching || notifications.length > 0) && hasUnreadNotifications && (
                  <Title.Actions>
                     <Button variant="secondary" onClick={clearAllNotifications}>
                        {I18n.t(
                           "views.company.workforce_planning.home.notifications.mark_all_read",
                        )}
                     </Button>
                  </Title.Actions>
               )}
            </Title>
            <Spinner
               centered
               loading={isFetching || isFetchingNextPage}
               className="notifications-card-spinner"
            >
               <Flex
                  direction="column"
                  gap="sm"
                  alignSelf="stretch"
                  style={{
                     overflowY: isFetching || isFetchingNextPage ? "hidden" : "scroll",
                     maxHeight: `calc(100vh - ${SCROLL_AREA_VERTICAL_SPACING})`,
                     scrollbarWidth: "auto",
                  }}
                  onScroll={scrollNotifications}
               >
                  {notifications.length > 0 && (
                     <>
                        {notifications.map((notification, index) => (
                           <Fragment key={notification.id}>
                              {(index === 0 ||
                                 new Date(notifications[index - 1].created_at).getDate() !==
                                    new Date(notification.created_at).getDate()) && (
                                 <Typography
                                    intent="body"
                                    weight="semibold"
                                    style={{ marginTop: "0.5rem" }}
                                 >
                                    {formatDateTime(new Date(notification.created_at), {
                                       locale: I18n.locale,
                                       dateStyle: "full",
                                    })}
                                 </Typography>
                              )}
                              <NotificationBanner
                                 notification={notification}
                                 onClick={
                                    notification.entity === NotificationEntity.ASSIGNMENT ||
                                    notification.entity === NotificationEntity.REQUEST ||
                                    ((notification.entity === NotificationEntity.PERSON ||
                                       notification.entity === NotificationEntity.PROJECT) &&
                                       notification.action !== NotificationAction.DELETE)
                                       ? () => navigateToNotification(notification)
                                       : undefined
                                 }
                              />
                           </Fragment>
                        ))}
                     </>
                  )}
                  {notifications.length === 0 && (
                     <Flex justifyContent="center" alignSelf="stretch">
                        <EmptyState>
                           <EmptyState.NoItemsWithoutCreatePermissions />
                           <EmptyState.Title>
                              {I18n.t(
                                 "views.company.workforce_planning.home.notifications.no_notifications",
                              )}
                           </EmptyState.Title>
                        </EmptyState>
                     </Flex>
                  )}
               </Flex>
            </Spinner>
         </Flex>
      </Card>
   );
};
